/**
 * TrazabilidadPage — recorrido de cada solicitud de teleconsulta (/admin/trazabilidad).
 *
 * Una fila por INTENTO del paciente, desde que entra al triage hasta cómo
 * terminó su atención. Incluye a quienes nunca llegaron a la cola —911,
 * presencial, región sin servicio, sin cupo, abandono— porque el embudo sin
 * ellos miente: solo se vería a quien tuvo suerte.
 *
 * Los datos llegan de `portal_getTrazabilidadTeleconsulta`, no de Firestore
 * directo: la tabla cruza `triage_eventos` con `consultas` y resuelve la cédula
 * desde `users/`, y nada de eso debe quedar al alcance del navegador.
 *
 * DATOS SENSIBLES. La pantalla muestra cédula y nombre. Es un reporte de
 * trazabilidad clínica para monitoreo, y cada consulta queda auditada con quién
 * la pidió y qué período miró.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Download, Loader2, RefreshCw, Route, Search } from 'lucide-react';

import MainLayout from '../../components/layout/MainLayout';
import { functions } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import dashStyles from './AdminDashboardPage.module.css';
import styles from './TrazabilidadPage.module.css';

const ZONA = 'America/Panama';

/** Hoy en Panamá — el día operativo, no el del navegador. */
function hoyPanama(desplazamientoDias = 0): string {
  const d = new Date(Date.now() + desplazamientoDias * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

type Preset = 'hoy' | '7d' | '30d' | 'custom';

const PRESETS: Array<{ key: Preset; label: string; dias: number }> = [
  { key: 'hoy', label: 'Hoy', dias: 0 },
  { key: '7d', label: 'Últimos 7 días', dias: 6 },
  { key: '30d', label: 'Últimos 30 días', dias: 29 },
];

export interface FilaTrazabilidad {
  consultaId: string | null;
  referencia: string;
  cedula: string | null;
  paciente: string | null;
  fecha: string;
  hora: string;
  nivelTriage: number | null;
  nivelNombre: string | null;
  resultadoTriage: string;
  motivoConsulta: string | null;
  region: string | null;
  sala: string | null;
  medico: string | null;
  estadoAtencion: string;
  motivoCierre: string | null;
  observacionesCierre: string | null;
  esperaMin: number | null;
  duracionMin: number | null;
  entroACola: boolean;
  inicioVideo: string | null;
  finAtencion: string | null;
  cerradaPor: string | null;
  reintentos: number | null;
  diagnosticoPrincipal: string | null;
}

interface Respuesta {
  filas: FilaTrazabilidad[];
  truncado: boolean;
  resumen: {
    total: number; entraronACola: number; completadas: number;
    noCompletadas: number; sinCupo: number;
  };
}

/**
 * Columnas: una sola definición para la tabla Y para el Excel.
 *
 * Tenerlas separadas garantizaba que tarde o temprano divergieran, y que lo
 * exportado dejara de coincidir con lo que la gente vio en pantalla.
 */
const COLUMNAS: Array<{
  clave: keyof FilaTrazabilidad;
  titulo: string;
  ancho: number;
  /** Solo en el Excel: en pantalla ocuparía demasiado. */
  soloExcel?: boolean;
}> = [
  { clave: 'referencia', titulo: 'Referencia', ancho: 12 },
  { clave: 'consultaId', titulo: 'ID de consulta', ancho: 34, soloExcel: true },
  { clave: 'cedula', titulo: 'Cédula', ancho: 14 },
  { clave: 'paciente', titulo: 'Paciente', ancho: 26 },
  { clave: 'fecha', titulo: 'Fecha', ancho: 12 },
  { clave: 'hora', titulo: 'Hora', ancho: 8 },
  { clave: 'nivelTriage', titulo: 'Nivel', ancho: 7 },
  { clave: 'nivelNombre', titulo: 'Clasificación', ancho: 18, soloExcel: true },
  { clave: 'resultadoTriage', titulo: 'Resultado del triage', ancho: 24 },
  { clave: 'motivoConsulta', titulo: 'Motivo de consulta', ancho: 34 },
  { clave: 'region', titulo: 'Región', ancho: 18 },
  { clave: 'sala', titulo: 'Sala', ancho: 18 },
  { clave: 'medico', titulo: 'Médico', ancho: 24 },
  { clave: 'estadoAtencion', titulo: 'Estado de la atención', ancho: 22 },
  { clave: 'motivoCierre', titulo: 'Motivo de cierre', ancho: 26 },
  { clave: 'observacionesCierre', titulo: 'Observaciones', ancho: 30, soloExcel: true },
  { clave: 'esperaMin', titulo: 'Espera (min)', ancho: 12 },
  { clave: 'duracionMin', titulo: 'Duración (min)', ancho: 13 },
  { clave: 'inicioVideo', titulo: 'Inicio del video', ancho: 14, soloExcel: true },
  { clave: 'finAtencion', titulo: 'Fin de atención', ancho: 14, soloExcel: true },
  { clave: 'cerradaPor', titulo: 'Cerrada por', ancho: 22, soloExcel: true },
  { clave: 'reintentos', titulo: 'Reintentos', ancho: 10, soloExcel: true },
  { clave: 'diagnosticoPrincipal', titulo: 'Diagnóstico principal', ancho: 30, soloExcel: true },
];

const EN_PANTALLA = COLUMNAS.filter((c) => !c.soloExcel);

/** Color del estado: lo que exige atención tiene que saltar sin leerse. */
function tonoEstado(estado: string): string {
  if (estado === 'Completada') return styles.tonoBien;
  if (estado === 'No completada') return styles.tonoMal;
  if (estado === 'En atención' || estado === 'En espera') return styles.tonoCurso;
  return styles.tonoNeutro;
}

export default function TrazabilidadPage() {
  const { addToast } = useToast();
  const [preset, setPreset] = useState<Preset>('hoy');
  const [desdeCustom, setDesdeCustom] = useState(hoyPanama(-6));
  const [hastaCustom, setHastaCustom] = useState(hoyPanama());
  const [datos, setDatos] = useState<Respuesta | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [exportando, setExportando] = useState(false);
  /** Si la solicitud llegó o no a crear una consulta. */
  const [filtroCupo, setFiltroCupo] = useState<'todas' | 'con' | 'sin'>('todas');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroMedico, setFiltroMedico] = useState('todos');
  /**
   * Columna y sentido del orden. Arranca sin definir para respetar el que trae
   * el servidor —cronológico descendente—, que es el que casi siempre se quiere
   * al abrir la pantalla.
   */
  const [orden, setOrden] = useState<{ col: keyof FilaTrazabilidad; dir: 'asc' | 'desc' } | null>(null);

  const { desde, hasta } = useMemo(() => {
    if (preset === 'custom') return { desde: desdeCustom, hasta: hastaCustom };
    const p = PRESETS.find((x) => x.key === preset)!;
    return { desde: hoyPanama(-p.dias), hasta: hoyPanama() };
  }, [preset, desdeCustom, hastaCustom]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const fn = httpsCallable<{ desde: string; hasta: string }, Respuesta>(
        functions, 'portal_getTrazabilidadTeleconsulta',
      );
      const res = await fn({ desde, hasta });
      setDatos(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la trazabilidad.');
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta]);

  useEffect(() => { void cargar(); }, [cargar]);

  /**
   * Opciones de los desplegables, derivadas de lo CARGADO y no de una lista
   * fija: así nunca ofrecen un médico o un estado que no aparece en el período,
   * ni omiten uno nuevo que el backend empiece a devolver.
   */
  const opciones = useMemo(() => {
    const f = datos?.filas ?? [];
    const uniq = (vals: Array<string | null>) =>
      [...new Set(vals.filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b));
    return {
      estados: uniq(f.map((r) => r.estadoAtencion)),
      medicos: uniq(f.map((r) => r.medico)),
    };
  }, [datos]);

  /** Filtros y orden: todo local, sobre lo YA cargado. */
  const filas = useMemo(() => {
    let f = datos?.filas ?? [];

    if (filtroCupo !== 'todas') f = f.filter((r) => r.entroACola === (filtroCupo === 'con'));
    if (filtroEstado !== 'todos') f = f.filter((r) => r.estadoAtencion === filtroEstado);
    if (filtroMedico !== 'todos') {
      f = filtroMedico === '__sin__'
        ? f.filter((r) => !r.medico)
        : f.filter((r) => r.medico === filtroMedico);
    }

    const q = busqueda.trim().toLowerCase();
    if (q) {
      f = f.filter((r) =>
        [r.cedula, r.paciente, r.medico, r.motivoConsulta, r.region, r.sala, r.referencia, r.estadoAtencion]
          .some((v) => (v ?? '').toString().toLowerCase().includes(q)));
    }

    if (orden) {
      const signo = orden.dir === 'asc' ? 1 : -1;
      f = [...f].sort((a, b) => {
        // La fecha se ordena junto con la hora: por sí sola agruparía el día
        // entero y dejaría las filas en un orden arbitrario dentro de él.
        const va = orden.col === 'fecha' ? `${a.fecha} ${a.hora}` : a[orden.col];
        const vb = orden.col === 'fecha' ? `${b.fecha} ${b.hora}` : b[orden.col];
        // Los vacíos van SIEMPRE al final, se ordene como se ordene: son
        // ausencia de dato, no el valor más pequeño.
        const aVacio = va === null || va === undefined || va === '';
        const bVacio = vb === null || vb === undefined || vb === '';
        if (aVacio && bVacio) return 0;
        if (aVacio) return 1;
        if (bVacio) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * signo;
        if (typeof va === 'boolean' && typeof vb === 'boolean') {
          return (Number(va) - Number(vb)) * signo;
        }
        return String(va).localeCompare(String(vb), 'es', { numeric: true }) * signo;
      });
    }
    return f;
  }, [datos, busqueda, filtroCupo, filtroEstado, filtroMedico, orden]);

  /** Un clic ordena ascendente; el siguiente invierte; el tercero lo quita. */
  const alternarOrden = useCallback((col: keyof FilaTrazabilidad) => {
    setOrden((prev) => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null;
    });
  }, []);

  const exportar = useCallback(async () => {
    if (filas.length === 0) return;
    setExportando(true);
    try {
      // Import dinámico: la librería solo se descarga cuando alguien exporta,
      // así no pesa en la carga de una pantalla que casi siempre solo se mira.
      // La entrada `/browser` es explícita: el paquete no expone raíz, y sin
      // ella el bundler no resuelve nada.
      const { default: writeXlsxFile } = await import('write-excel-file/browser');
      const NUMERICAS = new Set(['nivelTriage', 'esperaMin', 'duracionMin', 'reintentos']);
      // En el navegador `writeXlsxFile` no descarga: devuelve { toBlob, toFile }
      // y es `toFile` quien dispara la descarga con el nombre dado.
      await writeXlsxFile(filas, {
        columns: COLUMNAS.map((c) => ({
          width: c.ancho,
          header: { value: c.titulo, fontWeight: 'bold' as const, backgroundColor: '#E8F1EC' },
          cell: (r: FilaTrazabilidad) => {
            const v = r[c.clave];
            if (v === null || v === undefined || v === '') return null;
            if (NUMERICAS.has(c.clave)) return { value: Number(v), type: Number };
            // Todo lo demás va como TEXTO explícito: una cédula como
            // "8-882-370" a merced de la autodetección de Excel termina
            // convertida en fecha o en fórmula.
            return { value: typeof v === 'boolean' ? (v ? 'Sí' : 'No') : String(v), type: String };
          },
        })),
        stickyRowsCount: 1,
      }).toFile(`trazabilidad_${desde}_a_${hasta}.xlsx`);
      addToast('success', 'Archivo descargado');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo exportar');
    } finally {
      setExportando(false);
    }
  }, [filas, desde, hasta, addToast]);

  const r = datos?.resumen;

  return (
    <MainLayout>
      <div className={dashStyles.page}>
        <div className={dashStyles.header}>
          <div>
            <h1 className={dashStyles.title}>
              <Route size={22} aria-hidden="true" /> Trazabilidad de teleconsultas
            </h1>
            <p className={dashStyles.subtitle}>
              Recorrido de cada solicitud: desde la clasificación del triage hasta el cierre de la atención.
            </p>
          </div>
          <button
            type="button"
            className={styles.btnSecundario}
            onClick={() => void cargar()}
            disabled={cargando}
          >
            <RefreshCw size={15} className={cargando ? styles.girando : undefined} aria-hidden="true" />
            Actualizar
          </button>
        </div>

        {/* ── Período ── */}
        <div className={styles.rangeBar}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`${styles.presetBtn} ${preset === p.key ? styles.presetBtnActive : ''}`}
              onClick={() => setPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`${styles.presetBtn} ${preset === 'custom' ? styles.presetBtnActive : ''}`}
            onClick={() => setPreset('custom')}
          >
            Rango
          </button>
          {preset === 'custom' && (
            <div className={styles.customRange}>
              <input type="date" value={desdeCustom} max={hastaCustom}
                onChange={(e) => setDesdeCustom(e.target.value)} />
              <span className={styles.sepRango}>a</span>
              <input type="date" value={hastaCustom} min={desdeCustom} max={hoyPanama()}
                onChange={(e) => setHastaCustom(e.target.value)} />
            </div>
          )}

          <div className={styles.acciones}>
            <div className={styles.buscador}>
              <Search size={15} aria-hidden="true" />
              <input
                type="search"
                placeholder="Cédula, paciente, médico, región…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <button
              type="button"
              className={styles.btnExportar}
              onClick={() => void exportar()}
              disabled={exportando || filas.length === 0}
              title={filas.length === 0 ? 'No hay filas para exportar' : 'Descargar en Excel'}
            >
              {exportando
                ? <Loader2 size={15} className={styles.girando} aria-hidden="true" />
                : <Download size={15} aria-hidden="true" />}
              Exportar a Excel
            </button>
          </div>
        </div>

        {/* ── Filtros sobre lo cargado ──
            Van en su propia fila: el período decide QUÉ se trae del servidor,
            estos deciden qué se mira de lo traído. Mezclarlos en una sola barra
            hacía creer que cambiar un médico volvía a consultar. */}
        <div className={styles.filtrosBar}>
          <label className={styles.filtro}>
            <span>Solicitud de cita</span>
            <select value={filtroCupo} onChange={(e) => setFiltroCupo(e.target.value as typeof filtroCupo)}>
              <option value="todas">Todas</option>
              <option value="con">Sí generó consulta</option>
              <option value="sin">No generó consulta</option>
            </select>
          </label>

          <label className={styles.filtro}>
            <span>Estado de la atención</span>
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
              <option value="todos">Todos</option>
              {opciones.estados.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>

          <label className={styles.filtro}>
            <span>Médico</span>
            <select value={filtroMedico} onChange={(e) => setFiltroMedico(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="__sin__">Sin médico asignado</option>
              {opciones.medicos.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>

          {(filtroCupo !== 'todas' || filtroEstado !== 'todos' || filtroMedico !== 'todos'
            || busqueda || orden) && (
            <button
              type="button"
              className={styles.btnLimpiar}
              onClick={() => {
                setFiltroCupo('todas'); setFiltroEstado('todos');
                setFiltroMedico('todos'); setBusqueda(''); setOrden(null);
              }}
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {/* ── Embudo del período ── */}
        {r && (
          <div className={styles.resumen}>
            <Kpi valor={r.total} etiqueta="Solicitudes del triage" />
            <Kpi valor={r.entraronACola} etiqueta="Entraron a teleconsulta"
              nota={r.total > 0 ? `${Math.round((r.entraronACola / r.total) * 100)} % del total` : undefined} />
            <Kpi valor={r.completadas} etiqueta="Atenciones completadas" tono="bien" />
            <Kpi valor={r.noCompletadas} etiqueta="No completadas" tono={r.noCompletadas > 0 ? 'mal' : undefined} />
            <Kpi valor={r.sinCupo} etiqueta="Rechazadas por cupo" tono={r.sinCupo > 0 ? 'aviso' : undefined} />
          </div>
        )}

        {datos?.truncado && (
          <p className={styles.avisoTruncado}>
            El período supera el máximo de filas que se pueden traer de una vez. Se muestran las más
            recientes; para ver el resto, acote el rango de fechas.
          </p>
        )}

        {/* ── Tabla ── */}
        <div className={styles.tarjetaTabla}>
          {cargando ? (
            <p className={styles.estadoVacio}><Loader2 size={16} className={styles.girando} /> Cargando…</p>
          ) : error ? (
            <p className={styles.estadoError}>{error}</p>
          ) : filas.length === 0 ? (
            <p className={styles.estadoVacio}>
              {busqueda ? 'Ninguna fila coincide con la búsqueda.' : 'Sin solicitudes en el período.'}
            </p>
          ) : (
            <>
              <div className={styles.scrollTabla}>
                <table className={styles.tabla}>
                  <thead>
                    <tr>
                      {EN_PANTALLA.map((c) => {
                        const activa = orden?.col === c.clave;
                        return (
                          <th key={c.clave}>
                            <button
                              type="button"
                              className={`${styles.thBtn} ${activa ? styles.thBtnActiva : ''}`}
                              onClick={() => alternarOrden(c.clave)}
                              title={`Ordenar por ${c.titulo}`}
                              aria-sort={activa ? (orden!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                            >
                              {c.titulo}
                              <span className={styles.thFlecha} aria-hidden="true">
                                {activa ? (orden!.dir === 'asc' ? '▲' : '▼') : '↕'}
                              </span>
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f, i) => (
                      <tr key={`${f.consultaId ?? 'sc'}-${f.fecha}-${f.hora}-${i}`}>
                        {EN_PANTALLA.map((c) => {
                          const v = f[c.clave];
                          if (c.clave === 'estadoAtencion') {
                            return (
                              <td key={c.clave}>
                                <span className={`${styles.pill} ${tonoEstado(f.estadoAtencion)}`}>
                                  {f.estadoAtencion}
                                </span>
                              </td>
                            );
                          }
                          if (c.clave === 'nivelTriage' && f.nivelTriage) {
                            return (
                              <td key={c.clave}>
                                <span className={`${styles.nivel} ${styles[`nivel${f.nivelTriage}`]}`}>
                                  N{f.nivelTriage}
                                </span>
                              </td>
                            );
                          }
                          return (
                            <td key={c.clave} title={typeof v === 'string' ? v : undefined}>
                              {v === null || v === undefined || v === ''
                                ? <span className={styles.vacio}>—</span>
                                : String(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className={styles.pieTabla}>
                {filas.length} fila{filas.length === 1 ? '' : 's'}
                {busqueda && datos ? ` de ${datos.filas.length}` : ''} · el Excel incluye
                {' '}{COLUMNAS.length} columnas, con las que no caben en pantalla.
              </p>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

function Kpi({ valor, etiqueta, nota, tono }: {
  valor: number; etiqueta: string; nota?: string; tono?: 'bien' | 'mal' | 'aviso';
}) {
  return (
    <div className={styles.kpi}>
      <span className={`${styles.kpiValor} ${tono ? styles[`kpi_${tono}`] : ''}`}>{valor}</span>
      <span className={styles.kpiEtiqueta}>{etiqueta}</span>
      {nota && <span className={styles.kpiNota}>{nota}</span>}
    </div>
  );
}
